import {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useState,
} from "react"
import { ServerRenderedProps } from "./ServerRenderedProps"
import { sessionApiClient } from "@planningcenter/cc-api-client"
import { useQueryClient } from "@tanstack/react-query"
import { PersonAddress } from "../types"

type SessionPersonAuthenticated = {
  data: {
    id: string
    type: string
    attributes: {
      name: string
      first_name: string
      last_name: string
      address: PersonAddress | null
      email_address: string
      phone_number: string | null
      avatar_url: string
    }
  }
}

type SessionPersonAnonymous = {
  data: {
    id: null
    type: "AnonymousPerson"
    attributes: {
      name: "Unknown"
      first_name: "Unknown"
      last_name: "Unknown"
      address: null
      email_address: null
      phone_number: null
      avatar_url: string
    }
  }
}

type SessionPerson = SessionPersonAuthenticated | SessionPersonAnonymous

type Session = {
  current_person: SessionPerson
}

export interface DynamicSessionContextType {
  session: Session
  /** Refetch the session and clear the react-query cache */
  refreshSession: () => Promise<void>
}
export const DynamicSessionContext =
  createContext<DynamicSessionContextType | null>(null)

export function DynamicSessionProvider({ children }: { children: ReactNode }) {
  const { current_person: current_person_from_server_props } =
    useContext(ServerRenderedProps)
  const [session, setSession] = useState<Session>({
    current_person: current_person_from_server_props,
  })
  const queryClient = useQueryClient()

  const refreshSession = useCallback(async () => {
    console.debug("[DynamicSession] refreshing session now")
    const current_person = await sessionApiClient.check()
    setSession((prevData) => ({ ...prevData, current_person }))
    // Invalidate all queries to refresh cached data with new session
    queryClient.invalidateQueries()
  }, [queryClient])

  const value = { session, refreshSession }
  return (
    <DynamicSessionContext.Provider {...{ value }}>
      {children}
    </DynamicSessionContext.Provider>
  )
}

export function useDynamicSession() {
  const context = useContext(DynamicSessionContext)
  if (!context) {
    throw new Error(
      "useDynamicSession must be used within a DynamicSessionProvider",
    )
  }
  return context
}
